import { STORE } from '@/config';

/**
 * Locale to render a currency in. Formatting INR as en-US gives "₹399", but
 * formatting USD as en-IN gives "US$5" — so each currency is rendered in a
 * locale that writes its symbol the plain way.
 */
const LOCALES = { INR: 'en-IN' };

/**
 * A price with its currency. Both halves are required to mean anything: the
 * catalogue carries books priced in INR and books priced in USD, so "10" on its
 * own is not a price. `currency` is an ISO code (the API sends the code, never a
 * symbol) and the symbol is derived from it here.
 *
 * Fraction digits float between 0 and 2 so whole amounts stay clean ("₹399",
 * not "₹399.00") while a real fractional price keeps its cents ("$5.50").
 */
export function formatPrice(amount, currency = STORE.currency) {
  const code = currency || STORE.currency;
  try {
    return new Intl.NumberFormat(LOCALES[code] ?? 'en-US', {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount ?? 0);
  } catch {
    // An unknown or malformed code would otherwise throw and take the page with
    // it. Showing the code beside the number is honest and still readable.
    return `${code} ${Math.round(amount ?? 0).toLocaleString()}`;
  }
}

/** 4.6 → '★★★★★☆'-style string, matching the prototype's rounding. */
export function toStars(rating) {
  const filled = Math.round(rating);
  return '★'.repeat(filled) + '☆'.repeat(5 - filled);
}

/**
 * A product's headline price, in the product's own currency. Free books price at
 * 0, and "₹0" reads like a bug — say "Free" instead.
 */
export const priceLabel = (product) =>
  product.pricing?.is_free || product.price === 0
    ? 'Free'
    : formatPrice(product.price, product.currency);
